import React, { FC, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Col,
  Form,
  FormGroup,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import TextField from "../../shared/base-components/TextField";
import { SubmitHandler, useForm } from "react-hook-form";
import DatePicker from "../../shared/base-components/DatePicker";
import SelectBox from "../../shared/base-components/SelectBox";
import Button from "../../shared/base-components/Button";
import {
  useCustomerAddMutation,
  useDeviceListMutation,
  useDevicePartListMutation,
} from "./CustomerAdd.actions";
import {
  CustomerAddRequest,
  blankSchema,
  stepOneSchema,
  stepThreeSchema,
  stepTwoSchema,
} from "./CustomerAdd.constants";
import { yupResolver } from "@hookform/resolvers/yup";
import { RESPONSE_CODE } from "../../shared/models/Types";
import paths from "../../shared/route/paths";
import { successNotify } from "../../shared/utils/notifications";
import Switch from "../../shared/base-components/Switch";

import countries from "../../shared/utils/country.json";
import districties from "../../shared/utils/district.json";
import neigborhoods from "../../shared/utils/neigborhood.json";

import SearchableSelectBox from "../../shared/base-components/SearchableSelectBox";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  flutterMessageSelector,
  webViewSelector,
} from "../../shared/actions/LayoutMode.actions";
import { postUserAddLocation } from "../../shared/internals/asd";
import { customerListSelector } from "../../shared/actions/CustomerList.actions";

const CustomerAddWizard: FC = () => {
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const setCustomerList = useSetRecoilState(customerListSelector);
  const flutterMessage = useRecoilValue(flutterMessageSelector);
  const isWebView = useRecoilValue(webViewSelector);

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(1);
  const [currentSchema, setCurrentSchema] = useState<any>(stepOneSchema);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(currentSchema),
  });

  const { data: deviceListData, mutate: deviceListMutation } =
    useDeviceListMutation();

  const { data: devicePartListData, mutate: devicePartListMutation } =
    useDevicePartListMutation();

  const {
    data: customerAddData,
    mutate: customerAddMutation,
    isLoading: customerAddLoading,
  } = useCustomerAddMutation();

  const [guarentee, setGuarentee] = useState(false);
  const [payment, setPayment] = useState("ap");
  const [countryKey, setCountryKey] = useState<any>();
  const [districtKey, setDistrictKey] = useState<any>();
  const [firstSetup, setFirstSetup] = useState<boolean>(true);

  // const [selectedFilters, setSelectedFilters] = useState<any>();
  // const [selectedFilter, setSelectedFilter] = useState<any>();
  // const [selectedDevice, setSelectedDevice] = useState<string>("");,

  const onSubmit: SubmitHandler<any> = (requestValues: any) => {
    const selectedDeviceParts = deviceListData
      ?.find((device) => device?.id == requestValues?.deviceId)
      ?.device_parts.map((devicePart) => devicePart?.id)
      .join(",");

    let lat = "";
    let lng = "";

    if (flutterMessage.startsWith("musteriKonum:")) {
      const key = flutterMessage.split(":");

      lat = key[1].split("@")[0];
      lng = key[1].split("@")[1];
    }

    if (activeTab < 3) {
      if (Object.keys(errors).length === 0) {
        toggleTab(activeTab + 1);
      }
    } else {
      const requestForm: CustomerAddRequest = {
        customer_device: {
          device: requestValues.deviceId,
          date_of_setup: requestValues.date_of_setup,
          date_of_maintenance: requestValues.date_of_maintenance,
          warranty_start: requestValues.warranty_start,
          first_setup: firstSetup,
        },
        payment:
          payment === "y"
            ? undefined
            : {
                device: requestValues.deviceId,
                name_of_payment: requestValues.name_of_payment,
                note: requestValues.note,
                amount: requestValues.amount,
                payment_type: payment,
                number_of_installment: requestValues.number_of_installment,
                date_of_installment: requestValues.date_of_installment,
                got_amount: requestValues.got_amount,
              },
        device_part_ids: selectedDeviceParts?.length
          ? selectedDeviceParts
          : undefined,
        fullname: requestValues.fullname,
        description: requestValues.description,
        phone_number: requestValues.phone_number,
        phone_number2: requestValues?.phone_number2,
        phone_number3: requestValues?.phone_number3,
        country: requestValues.country,
        email: requestValues.email,
        district: requestValues.district,
        neighbourhood: requestValues.neighbourhood,
        street: requestValues.street,
        flat_number: requestValues.flat_number,
        lang: lat.length ? lat : undefined,
        long: lng.length ? lng : undefined,
        address_description: requestValues.address_description,
      };

      customerAddMutation(requestForm);
    }
  };

  const toggleTab = (tab: number) => {
    if (tab >= 1 && tab <= 3) {
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    deviceListMutation({});
    devicePartListMutation({});
  }, []);

  useEffect(() => {
    if (customerAddData?.status === RESPONSE_CODE.CREATED) {
      setCustomerList((prevState) => ({
        ...prevState,
        count: prevState.count + 1,
        results: [customerAddData, ...prevState.results],
      }));

      navigate(paths.CUSTOMER.CUSTORMER_LIST);

      successNotify("Müşteri başarılı bir şekilde oluşuruldu.");
    }
  }, [customerAddData]);

  useEffect(() => {
    switch (activeTab) {
      case 1:
        setCurrentSchema(stepOneSchema);
        break;
      case 2:
        setCurrentSchema(stepTwoSchema);
        break;
      case 3:
        payment === "y"
          ? setCurrentSchema(blankSchema)
          : setCurrentSchema(stepThreeSchema);
        break;
      default:
        setCurrentSchema(stepOneSchema);
    }
  }, [activeTab, payment]);

  const valueData = deviceListData?.map((device) => ({
    key: device?.name.toString(),
    value: device?.id,
  }));

  // const getFormattedDevicePartResponse = devicePartListData?.map(
  //   (devicePart: any) => {
  //     return { key: devicePart?.name, value: devicePart?.id };
  //   }
  // );

  const countryValues = countries.map((country) => ({
    value: country.sehir_title,
  }));

  const findDistricByCountry = (countryKey: string) => {
    const filteredDistricties = districties.filter(
      (district) => district.ilce_sehirkey === countryKey
    );

    const districtValuessss = filteredDistricties.map((district) => ({
      value: district.ilce_title,
    }));

    return districtValuessss;
  };

  const findNeigborhoodByCountry = (districtKey: string) => {
    const filteredNeigbor = neigborhoods.filter(
      (neigbor) => neigbor?.id === districtKey
    );

    const neigborhoodsValues = filteredNeigbor.map((a) => ({
      value: a.ad,
    }));

    return neigborhoodsValues;
  };

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit(onSubmit)} className="custom-form mt-4 pt-2">
        <div id="basic-pills-wizard" className="twitter-bs-wizard">
          <ul className="twitter-bs-wizard-nav nav nav-pills nav-justified">
            <NavItem>
              <NavLink
                href="#"
                className={`${activeTab === 1 ? "active" : ""}`}
              >
                <div
                  className="step-icon"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="SellerDetails"
                >
                  <i className="bx bx-list-ul"></i>
                  <UncontrolledTooltip placement="top" target="SellerDetails">
                    İletişim Bilgileri
                  </UncontrolledTooltip>
                </div>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                className={`${activeTab === 2 ? "active" : ""}`}
              >
                <div
                  className="step-icon"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="CompanyDocument"
                >
                  <i className="bx bx-book-bookmark"></i>
                  <UncontrolledTooltip placement="top" target="CompanyDocument">
                    Makine Bilgileri
                  </UncontrolledTooltip>
                </div>
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                href="#"
                className={`${activeTab === 3 ? "active" : ""}`}
              >
                <div
                  className="step-icon"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="BankDetails"
                >
                  <i className="bx bxs-bank"></i>
                  <UncontrolledTooltip placement="top" target="BankDetails">
                    Ödeme Bilgileri
                  </UncontrolledTooltip>
                </div>
              </NavLink>
            </NavItem>
          </ul>
          <TabContent
            className="twitter-bs-wizard-tab-content"
            activeTab={activeTab}
          >
            <TabPane tabId={1}>
              <div className="text-center mb-4">
                <h5> İletişim Bilgileri</h5>
              </div>
              <Row>
                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label className="form-label">Ad ve Soyad</Label>
                    <TextField
                      control={control}
                      name="fullname"
                      type="text"
                      className="form-control"
                      placeholder="Ad Soyad"
                      error={errors.fullname?.message}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">Cep Telefonu</Label>
                    <TextField
                      control={control}
                      name="phone_number"
                      className="form-control"
                      type="text"
                      placeholder="0--- --- -- --"
                      error={errors.phone_number?.message}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">
                      Cep Telefonu 2 (Opsiyonel)
                    </Label>
                    <TextField
                      control={control}
                      name="phone_number2"
                      className="form-control"
                      type="text"
                      placeholder="0--- --- -- --"
                      error={errors.phone_number2?.message}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">
                      Cep Telefonu 3 (Opsiyonel)
                    </Label>
                    <TextField
                      control={control}
                      name="phone_number3"
                      className="form-control"
                      type="text"
                      placeholder="0--- --- -- --"
                      error={errors.phone_number3?.message}
                    />
                  </FormGroup>
                </Col>

                {/* <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">Email</Label>
                    <TextField
                      control={control}
                      name="email"
                      className="form-control"
                      type="text"
                      placeholder="Email"
                      error={errors.email?.message}
                    />
                  </FormGroup>
                </Col> */}

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label className="form-label">İl</Label>
                    <SearchableSelectBox
                      control={control}
                      name="country"
                      id="country"
                      noValid
                      className="form-control"
                      onBlur={(e) =>
                        setCountryKey(
                          countries.find(
                            (country) => country.sehir_title === e.target.value
                          )
                        )
                      }
                      options={countryValues}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label>İlçe</Label>

                    <SearchableSelectBox
                      control={control}
                      name="district"
                      id="district"
                      onBlur={(e) =>
                        setDistrictKey(
                          districties.find(
                            (district) => district.ilce_title === e.target.value
                          )?.ilce_key
                        )
                      }
                      noValid
                      className="form-control"
                      options={findDistricByCountry(countryKey?.sehir_key)}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label className="form-label">Mahalle</Label>
                    <SearchableSelectBox
                      control={control}
                      name="neighbourhood"
                      id="neighbourhood"
                      noValid
                      className="form-control"
                      options={findNeigborhoodByCountry(districtKey)}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">Cadde / Sokak</Label>
                    <TextField
                      control={control}
                      name="street"
                      className="form-control"
                      type="text"
                      placeholder="Cadde / Sokak"
                      error={errors.street?.message}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label className="form-label">Daire / Numara</Label>
                    <TextField
                      control={control}
                      name="flat_number"
                      type="text"
                      className="form-control"
                      placeholder="Daire / Numara"
                      error={errors.flat_number?.message}
                    />
                  </FormGroup>
                </Col>

                <Col sm="12" md="6" lg="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="validationCustom02">Kısa Tarif</Label>
                    <TextField
                      control={control}
                      name="address_description"
                      className="form-control"
                      type="text"
                      placeholder="Kısa Tarif"
                      error={errors.address_description?.message}
                    />
                  </FormGroup>
                </Col>

                {isWebView && (
                  <Col sm="12" md="6" lg="6">
                    <FormGroup className="mb-3">
                      <Label htmlFor="validationCustom02">Konum seç</Label>

                      <Button
                        text="Konum Ekle"
                        type="button"
                        variant="primary"
                        onClick={() => postUserAddLocation(`konumVerKanka`)}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </TabPane>
            <TabPane tabId={2}>
              <div>
                <div className="text-center mb-4">
                  <h5>Makine Bilgileri</h5>
                </div>
                <Row>
                  <Col sm="12" md="6" lg="6">
                    <FormGroup className="mb-3">
                      <Label className="form-label">Montaj Tarihi</Label>
                      <DatePicker
                        control={control}
                        name="date_of_setup"
                        type="text"
                        className="form-control"
                        defaultValue={new Date()}
                        error={errors.date_of_setup?.message}
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="4" lg="4">
                    <FormGroup className="mb-3">
                      <Label htmlFor="validationCustom02">Cihaz Modeli</Label>
                      <SelectBox
                        control={control}
                        name="deviceId"
                        className="form-control"
                        defaultValue="-1"
                        options={[
                          {
                            key: "Lütfen cihaz seçin",
                            value: -1,
                          },
                          ...(valueData || []),
                        ]}
                        error={errors.deviceId?.message}
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="2" lg="2">
                    <Label htmlFor="validationCustom02">İlk Kurulum</Label>

                    <FormGroup className="mb-3">
                      <Switch
                        onChangeHandler={(e: any) => setFirstSetup(e)}
                        name="firstSetup"
                        control={control}
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="6" lg="6">
                    <FormGroup className="mb-3">
                      <Label htmlFor="validationCustom02">
                        Bir sonraki bakım Tarihi
                      </Label>
                      <DatePicker
                        control={control}
                        name="date_of_maintenance"
                        className="form-control"
                        type="text"
                        defaultValue={new Date()}
                        placeholder="Bir sonraki bakım Tarihi"
                        error={errors.date_of_maintenance?.message}
                      />
                    </FormGroup>
                  </Col>

                  <Col sm="12" md="6" lg="6">
                    <FormGroup className="mb-3">
                      <Label htmlFor="validationCustom02">
                        Garanti Bilgileri
                      </Label>
                      <SelectBox
                        name="salary_gross"
                        className="form-control"
                        onChangeHandler={(e) =>
                          e.target.value === "have"
                            ? setGuarentee(true)
                            : setGuarentee(false)
                        }
                        options={[
                          { key: "Garanti Yok", value: "notHave" },
                          { key: "Garanti Var", value: "have" },
                        ]}
                      />
                    </FormGroup>
                  </Col>

                  {guarentee && (
                    <>
                      <Col sm="12" md="6" lg="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom02">
                            Garanti Başlangıç Tarihi
                          </Label>
                          <DatePicker
                            control={control}
                            name="warranty_start"
                            className="form-control"
                            type="text"
                            defaultValue={new Date()}
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>

                {/* {selectedDevice && (
                  <Col sm="12" md="12" lg="12">
                    <FormGroup className="mb-3">
                      <Label className="form-label">Malzeme Ekle</Label>

                      <Card className={"mb-xl-0"}>
                        <CardBody>
                          {!selectedFilters.length && (
                            <p className={"text-muted mt-3 font-size-15"}>
                              Eklemek istediğiniz malzemeyi seçiniz.
                            </p>
                          )}

                          <Col sm="12" md="12" lg="12">
                            <ul
                              style={{ padding: "2" }}
                              className="list-unstyled  mb-4"
                            >
                              {selectedFilters?.map(
                                (selectedFiltre: any, index: number) => {
                                  return (
                                    <li
                                      style={{
                                        borderBottom: "1px solid #7b8198",
                                        paddingBottom: 11,
                                      }}
                                      key={selectedFiltre}
                                    >
                                      <div className="d-flex align-items-start">
                                        <div className="flex-grow-1 overflow-hidden h-2">
                                          <h5 className="text-truncate font-size-14 mt-2">
                                            {
                                              getFormattedDevicePartResponse?.find(
                                                (devicePart) =>
                                                  devicePart.value ==
                                                  selectedFiltre
                                              )?.key
                                            }
                                          </h5>
                                        </div>

                                        <div className="d-flex align-items-start justify-content-end gap-3 mt-2">
                                          <Link
                                            to={"#"}
                                            onClick={() => {
                                              const selectedIndex =
                                                selectedFilters.indexOf(
                                                  selectedFiltre
                                                );

                                              if (selectedIndex !== -1) {
                                                selectedFilters.splice(
                                                  selectedIndex,
                                                  1
                                                );
                                              }
                                            }}
                                            className="badge btn btn-danger rounded-pill font-size-10 p-2 pointer"
                                          >
                                            <i className="mdi mdi-close noti-icon"></i>{" "}
                                            Sil
                                          </Link>
                                        </div>
                                      </div>
                                    </li>
                                  );
                                }
                              )}
                            </ul>
                          </Col>

                          <div className={"mt-4 pt-2 text-muted"}>
                            <Col sm="12" md="12" lg="12">
                              <FormGroup className="mb-3">
                                <Label className="form-label">
                                  Malzeme Ekle
                                </Label>
                                <Card className={"mb-xl-0"}>
                                  <CardBody>
                                    <Row>
                                      <Col sm="12" md="6" lg="6">
                                        {getFormattedDevicePartResponse && (
                                          <SelectBox
                                            name="devicePart"
                                            className="form-control"
                                            defaultValue="0"
                                            onChangeHandler={(e) =>
                                              setSelectedFilter(e.target.value)
                                            }
                                            options={[
                                              ...getFormattedDevicePartResponse,
                                              {
                                                key: "Lütfen malzeme seçin",
                                                value: -1,
                                              },
                                            ]}
                                          />
                                        )}
                                      </Col>

                                      <Col
                                        className={isMobile ? "mt-3" : ""}
                                        sm="12"
                                        md="6"
                                        lg="6"
                                      >
                                        <Link
                                          onClick={() =>
                                            selectedFilter > 0 &&
                                            setSelectedFilters(
                                              (prevState: any) => [
                                                ...prevState,
                                                parseInt(selectedFilter),
                                              ]
                                            )
                                          }
                                          to="#"
                                          className={"btn btn-success w-100"}
                                        >
                                          Malzeme Ekle
                                        </Link>
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              </FormGroup>
                            </Col>
                          </div>
                        </CardBody>
                      </Card>
                    </FormGroup>
                  </Col>
                )}

                {selectedDevice && <></>} */}
              </div>
            </TabPane>

            <TabPane tabId={3}>
              <div>
                <div className="text-center mb-4">
                  <h5>Ödeme Bilgileri</h5>
                </div>
                <Row>
                  <Col sm="12" md="6" lg="6">
                    <FormGroup className="mb-3">
                      <Label htmlFor="validationCustom02">Ödeme Tipi</Label>
                      <SelectBox
                        control={control}
                        name="payment_type"
                        className="form-control"
                        onChangeHandler={(e) => setPayment(e.target.value)}
                        options={[
                          { key: "Peşin Ödeme", value: "ap" },
                          { key: "Taksitli Ödeme", value: "ip" },
                          { key: "Ödeme Yok", value: "y" },
                        ]}
                      />
                    </FormGroup>
                  </Col>

                  {payment !== "y" && (
                    <>
                      <Col sm="12" md="6" lg="6">
                        <FormGroup className="mb-3">
                          <Label className="form-label">Ödeme Adı</Label>
                          <TextField
                            control={control}
                            name="name_of_payment"
                            type="text"
                            className="form-control"
                            placeholder="Ödeme Adı"
                            error={errors.name_of_payment?.message}
                          />
                        </FormGroup>
                      </Col>

                      <Col sm="12" md="6" lg="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom02">
                            Yapılan İş Ücreti
                          </Label>
                          <TextField
                            control={control}
                            name="amount"
                            className="form-control"
                            type="number"
                            placeholder="Yapılan İş Ücreti"
                            error={errors.amount?.message}
                          />
                        </FormGroup>
                      </Col>

                      <Col sm="12" md="6" lg="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom02">
                            Alınan İş Ücreti
                          </Label>
                          <TextField
                            control={control}
                            name="got_amount"
                            className="form-control"
                            type="number"
                            placeholder="Alınan İş Ücreti"
                            error={errors.got_amount?.message}
                          />
                        </FormGroup>
                      </Col>

                      {payment === "ip" && (
                        <>
                          <Col sm="12" md="6" lg="6">
                            <FormGroup className="mb-3">
                              <Label htmlFor="validationCustom02">
                                Taksit Başlangıç Tarihi
                              </Label>
                              <DatePicker
                                control={control}
                                name="date_of_installment"
                                className="form-control"
                                defaultValue={new Date()}
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="12" md="6" lg="6">
                            <FormGroup className="mb-3">
                              <Label htmlFor="validationCustom02">
                                Taksit Sayısı
                              </Label>
                              <TextField
                                control={control}
                                name="number_of_installment"
                                className="form-control"
                                type="number"
                                placeholder="Taksit Sayısı"
                                error={errors.number_of_installment?.message}
                              />
                            </FormGroup>
                          </Col>
                        </>
                      )}

                      <Col sm="12" md="6" lg="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom02">Not</Label>
                          <TextField
                            control={control}
                            name="note"
                            className="form-control"
                            type="text"
                            placeholder="Not"
                            error={errors.note?.message}
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            </TabPane>
          </TabContent>
          <ul className="pager wizard twitter-bs-wizard-pager-link">
            <li className={activeTab === 1 ? "previous disabled" : "previous"}>
              <Link
                to="#"
                className={
                  activeTab === 1
                    ? "btn btn-primary disabled"
                    : "btn btn-primary"
                }
                onClick={() => {
                  toggleTab(activeTab - 1);
                }}
              >
                <i className="bx bx-chevron-left me-1"></i> Geri
              </Link>
            </li>

            {activeTab === 3 ? (
              <li className={activeTab === 3 ? "next disabled" : "next"}>
                <Button
                  variant="primary"
                  text="Tamamla"
                  iconSrc="check-double"
                  loading={customerAddLoading}
                  onClick={() => {
                    toggleTab(activeTab + 1);
                  }}
                ></Button>
              </li>
            ) : (
              <li className={activeTab === 3 ? "next disabled" : "next"}>
                <Button
                  // className="btn btn-primary"
                  iconSrc="chevron-right"
                  text="İleri"
                  variant="primary"
                ></Button>
              </li>
            )}
          </ul>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default CustomerAddWizard;
